import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Check, Leaf, ShieldCheck, Quote, ChevronLeft, ChevronRight, Sprout, Award, Microscope, Ship, Package, BadgeIndianRupee, FlaskConical, Scissors, Wind } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/lib/motion";
import Counter from "@/components/Counter";
import WorldMap from "@/components/WorldMap";
import InquiryForm from "@/components/InquiryForm";
import {
  IMAGES, HERO_SLIDES, STORY, STATS, WHY, MANIFESTO, TIMELINE, INDUSTRIES, PACKAGING,
  SUSTAINABILITY, TESTIMONIALS, FAQ, CERTS,
} from "@/lib/data";
import { getCategories } from "@/lib/api";

const WHY_ICONS = [Sprout, Award, Leaf, ShieldCheck, Microscope, Ship, Package, BadgeIndianRupee];

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return <motion.div style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[3px] bg-gold origin-left z-[60]" />;
}


function Particles() {
  const dots = Array.from({ length: 14 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {dots.map((_, i) => {
        const left = (i * 137) % 100;
        const size = 2 + (i % 4);
        const dur = 9 + (i % 6) * 2;
        return (
          <motion.span key={i}
            className="absolute rounded-full bg-gold/40"
            style={{ left: `${left}%`, width: size, height: size, bottom: -10 }}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: [-20, -560], opacity: [0, 0.8, 0] }}
            transition={{ duration: dur, repeat: Infinity, delay: (i % 7) * 1.1, ease: "easeInOut" }}
          />
        );
      })}
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.32, 0.7]);
  const [slide, setSlide] = useState(0);
  const go = (n) => setSlide((s) => (s + n + HERO_SLIDES.length) % HERO_SLIDES.length);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [slide]);

  const s = HERO_SLIDES[slide];

  return (
    <section ref={ref} className="relative h-screen overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={slide} src={s.src} alt={s.alt}
            initial={{ opacity: 0, scale: 1.12 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6, ease: "easeOut" } }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-forest" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/20 to-forest/40" />
      <Particles />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="overline text-gold mb-6">
          Indian Agricultural Exporter · Est. Heritage
        </motion.p>

        <AnimatePresence mode="wait">
          <motion.div key={slide}
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="font-serif text-beige text-[10vw] md:text-[5.6vw] leading-[1.0] tracking-tight max-w-5xl">
              {s.headline}
            </h1>
            <p className="text-beige/85 max-w-xl mt-6 text-base md:text-lg font-light">{s.sub}</p>
          </motion.div>
        </AnimatePresence>

        <div className="flex flex-wrap gap-4 mt-10">
          {s.href.startsWith("/") ? (
            <Link to={s.href} data-testid="hero-cta-btn"
              className="group flex items-center gap-3 bg-gold text-forest px-8 py-4 text-sm uppercase tracking-widest font-medium rounded-full hover:bg-beige transition-colors">
              {s.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          ) : (
            <a href={s.href} data-testid="hero-cta-btn"
              className="group flex items-center gap-3 bg-gold text-forest px-8 py-4 text-sm uppercase tracking-widest font-medium rounded-full hover:bg-beige transition-colors">
              {s.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
          <Link to="/products" data-testid="hero-explore-btn"
            className="flex items-center gap-3 border border-beige/40 text-beige px-8 py-4 text-sm uppercase tracking-widest font-medium rounded-full hover:bg-beige hover:text-forest transition-colors">
            All Products
          </Link>
        </div>
      </div>

      {/* Navigation arrows */}
      <button data-testid="hero-prev" onClick={() => go(-1)} aria-label="Previous slide"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-beige/40 text-beige flex items-center justify-center hover:bg-gold hover:border-gold hover:text-forest transition-colors">
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button data-testid="hero-next" onClick={() => go(1)} aria-label="Next slide"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full border border-beige/40 text-beige flex items-center justify-center hover:bg-gold hover:border-gold hover:text-forest transition-colors">
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Pagination dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} data-testid={`hero-slide-${i}`} onClick={() => setSlide(i)} aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-500 ${i === slide ? "w-8 bg-gold" : "w-2 bg-beige/40 hover:bg-beige/70"}`} />
        ))}
      </div>
    </section>
  );
}

const TIMELINE_STEPS = [
  { icon: Sprout, label: "Farm" },
  { icon: Scissors, label: "Harvest" },
  { icon: Wind, label: "Cleaning" },
  { icon: FlaskConical, label: "Quality Testing" },
  { icon: Package, label: "Packaging" },
  { icon: Ship, label: "Export" },
];

function Storytelling() {
  return (
    <section className="bg-beige py-24 md:py-32" data-testid="story">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-16 md:mb-24 text-center mx-auto">
          <Reveal><p className="overline text-olive mb-4">Our Journey</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl md:text-6xl text-forest leading-[1.05]">
              From <span className="italic text-gold">Soil</span> to <span className="italic text-gold">Shipment</span>
            </h2>
          </Reveal>
        </div>

        <div className="space-y-20 md:space-y-28">
          {STORY.map((s, i) => (
            <div key={s.n} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
              <Reveal y={50} className="md:[direction:ltr]">
                <div className="relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-20px_rgba(27,59,54,0.35)] group">
                  <img src={s.img} alt={s.alt} loading="lazy"
                    className="w-full h-[300px] md:h-[420px] object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/40 to-transparent" />
                </div>
              </Reveal>
              <Reveal delay={0.1} y={50} className="md:[direction:ltr]">
                <span className="font-serif text-6xl md:text-7xl text-gold/30 leading-none">{s.n}</span>
                <h3 className="font-serif text-4xl md:text-5xl text-forest mt-2 mb-5 leading-tight">{s.title}</h3>
                <p className="text-ink/70 text-lg font-light leading-relaxed max-w-lg">{s.text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessTimeline() {
  return (
    <section className="bg-forest text-beige py-24 relative grain overflow-hidden" data-testid="process-timeline">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <Reveal><p className="overline text-gold mb-4">Farm-to-Freight</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl">Six steps. One promise of purity.</h2></Reveal>
        </div>
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-10 md:gap-4">
          <div className="hidden md:block absolute top-9 left-[8%] right-[8%] h-px bg-beige/20" />
          {TIMELINE_STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div key={step.label}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="relative z-10 flex flex-col items-center text-center flex-1">
                <div className="w-[72px] h-[72px] rounded-full bg-olive border-2 border-gold flex items-center justify-center mb-4 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)]">
                  <Icon className="w-7 h-7 text-gold" />
                </div>
                <span className="font-serif text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-medium text-sm md:text-base">{step.label}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="bg-olive text-beige py-24 relative grain overflow-hidden" data-testid="stats">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-12">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="text-center lg:text-left border-l border-beige/20 pl-6">
              <div className="font-serif text-6xl md:text-7xl text-gold leading-none">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-3 text-sm text-beige/70 uppercase tracking-wider">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section id="about" className="py-28 md:py-36 max-w-7xl mx-auto px-6" data-testid="about">
      <Reveal><p className="overline text-olive mb-4">Who We Are</p></Reveal>
      <div className="grid lg:grid-cols-12 gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-6xl text-forest leading-[1.05]">
              A house built on <span className="italic text-gold">soil, trust</span> and standards.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 overflow-hidden">
              <img src={IMAGES.farm} alt="Indian farm landscape" className="w-full h-80 object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 space-y-14">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05} className="flex gap-8 border-b border-forest/10 pb-12">
              <span className="font-serif text-4xl text-gold shrink-0">{m.n}</span>
              <div>
                <h3 className="font-serif text-3xl text-forest mb-3">{m.t}</h3>
                <p className="text-ink/70 leading-relaxed font-light">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = ["Spices", "Herbal Powders", "Extracts", "Millets", "Pulses", "Cereals", "Oil Seeds", "Dehydrated"];
  return (
    <section className="bg-forest py-10 border-y border-beige/10" data-testid="marquee">
      <Marquee speed={40} gradient={false}>
        {words.concat(words).map((w, i) => (
          <span key={i} className="font-serif text-6xl md:text-7xl text-outline mx-8 italic">{w} <span className="text-gold not-italic">·</span></span>
        ))}
      </Marquee>
    </section>
  );
}

function ProductsPreview() {
  const [cats, setCats] = useState([]);
  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);
  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6"];

  return (
    <section className="py-28 md:py-36 bg-beige" data-testid="products-preview">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
          <div>
            <Reveal><p className="overline text-olive mb-4">Our Portfolio</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-5xl md:text-6xl text-forest leading-tight max-w-2xl">
                Eight categories. <span className="italic text-gold">One standard</span> of excellence.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/products" data-testid="view-all-products"
              className="group flex items-center gap-3 text-forest text-sm uppercase tracking-widest border-b border-forest pb-1 hover:text-gold hover:border-gold transition-colors">
              View All Products <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          {cats.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 3) * 0.08} className={spans[i] || "lg:col-span-4"}>
              <Link to={`/products?category=${c.slug}`} data-testid={`cat-card-${c.slug}`}
                className="group block relative overflow-hidden h-72 md:h-80">
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/20 to-transparent" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end">
                  <span className="overline text-gold mb-2">{c.count} Products</span>
                  <h3 className="font-serif text-3xl text-beige">{c.name}</h3>
                  <p className="text-beige/0 group-hover:text-beige/80 max-h-0 group-hover:max-h-20 overflow-hidden transition-all duration-500 text-sm mt-1 font-light">{c.tagline}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="py-28 md:py-36 bg-white" data-testid="why-choose">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal><p className="overline text-olive mb-4">Why Karmaa Overseas</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-5xl md:text-6xl text-forest max-w-3xl leading-tight mb-16">
            Everything a global buyer needs, <span className="italic text-gold">under one roof.</span>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY.map((w, i) => {
            const Icon = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <Reveal key={w.t} delay={(i % 4) * 0.08} className="card-soft p-8 group min-h-[230px]">
                <div className="w-14 h-14 rounded-full bg-olive/10 flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-500">
                  <Icon className="w-6 h-6 text-olive group-hover:text-forest transition-colors duration-500" />
                </div>
                <h3 className="font-serif text-2xl text-forest mb-3">{w.t}</h3>
                <p className="text-ink/60 text-sm leading-relaxed font-light">{w.d}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MapSection() {
  return (
    <section id="map" className="py-28 md:py-36 bg-forest text-beige relative grain overflow-hidden" data-testid="map-section">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal><p className="overline text-gold mb-4">Global Reach</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl md:text-6xl leading-tight">
              From Indian fields to <span className="italic text-gold">the world’s shelves.</span>
            </h2>
          </Reveal>
        </div>
        <WorldMap />
      </div>
    </section>
  );
}

function Quality() {
  return (
    <section id="quality" className="py-28 md:py-36 bg-beige" data-testid="quality">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
          <Reveal><p className="overline text-olive mb-4">Quality Control</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl md:text-6xl text-forest leading-tight">
              Ten checkpoints from <span className="italic text-gold">farm to freight.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-ink/70 font-light leading-relaxed">
              Every consignment moves through a disciplined chain of inspection, cleaning, testing and documentation — so what leaves India meets the exact spec that lands at your port.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <div className="relative border-l border-forest/15 ml-4">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.step} delay={i * 0.04} className="relative pl-10 pb-8 last:pb-0">
                <span className="absolute -left-[7px] top-1 w-3.5 h-3.5 rounded-full bg-gold ring-4 ring-beige" />
                <div className="flex items-center gap-5 group">
                  <div className="w-24 h-20 md:w-32 md:h-24 shrink-0 overflow-hidden">
                    <img src={t.img} alt={t.step} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div>
                    <span className="font-serif text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight">{t.step}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Industries() {
  return (
    <section className="py-24 bg-white" data-testid="industries">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal><p className="overline text-olive mb-4">Industry Applications</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-5xl text-forest mb-12 max-w-2xl">Serving the industries that feed & heal the world.</h2>
        </Reveal>
        <div className="flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind} delay={i * 0.04} as="span"
              className="px-6 py-3 border border-forest/20 text-forest text-sm font-medium hover:bg-forest hover:text-beige transition-colors cursor-default">
              {ind}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packaging() {
  return (
    <section className="py-28 bg-beige" data-testid="packaging">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal><p className="overline text-olive mb-4">Packaging Options</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-5xl text-forest mb-14 max-w-2xl">Packed to protect potency — and your brand.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-forest/10">
          {PACKAGING.map((p, i) => (
            <Reveal key={p.t} delay={(i % 3) * 0.08} className="bg-beige p-8 hover:bg-white transition-colors">
              <span className="font-serif text-gold text-2xl">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif text-2xl text-forest mt-2 mb-2">{p.t}</h3>
              <p className="text-ink/60 text-sm font-light">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Sustainability() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative py-32 overflow-hidden" data-testid="sustainability">
      <motion.img style={{ y, scale: 1.2 }} src={IMAGES.sowing} alt="Farmer sowing seeds" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-forest/85 grain" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-beige">
        <Reveal><p className="overline text-gold mb-4">Sustainability</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-5xl md:text-6xl max-w-3xl leading-tight mb-16">Responsible agriculture is the only agriculture we practice.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {SUSTAINABILITY.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.1} className="border-t border-gold/40 pt-5">
              <Check className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-serif text-2xl mb-2">{s.t}</h3>
              <p className="text-beige/70 text-sm font-light">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CertMarquee() {
  return (
    <section className="bg-olive py-16" data-testid="certifications">
      <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
        <p className="overline text-beige/70">Certified & Compliant</p>
      </div>
      <Marquee speed={50} gradient={false}>
        {CERTS.concat(CERTS).map((c, i) => (
          <span key={i} className="mx-8 font-serif text-4xl md:text-5xl text-beige/90 flex items-center gap-8">
            {c}<span className="text-gold">◆</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

function Testimonials() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="py-28 md:py-36 bg-white" data-testid="testimonials">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Quote className="w-12 h-12 text-gold mx-auto mb-8" />
        <div className="min-h-[220px] flex items-center">
          <AnimatePresence mode="wait">
            <motion.div key={idx}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}>
              <p className="font-serif text-3xl md:text-4xl text-forest leading-snug italic">“{TESTIMONIALS[idx].q}”</p>
              <p className="mt-8 text-forest font-medium">{TESTIMONIALS[idx].n}</p>
              <p className="text-ink/50 text-sm">{TESTIMONIALS[idx].r}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex justify-center gap-2 mt-8">
          {TESTIMONIALS.map((_, i) => (
            <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => setIdx(i)}
              className={`h-1.5 transition-all ${i === idx ? "w-8 bg-gold" : "w-4 bg-forest/20"}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="py-28 bg-beige" data-testid="faq">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal><p className="overline text-olive mb-4 text-center">FAQ</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-serif text-5xl text-forest text-center mb-14">Answers for buyers.</h2></Reveal>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-forest/15" data-testid={`faq-${i}`}>
              <AccordionTrigger className="font-serif text-xl md:text-2xl text-forest text-left hover:text-gold hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink/70 text-base font-light leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="py-28 md:py-36 bg-forest text-beige relative grain overflow-hidden" data-testid="contact">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16 relative z-10">
        <div className="lg:col-span-5">
          <p className="overline text-gold mb-4">Request a Quote</p>
          <h2 className="font-serif text-5xl md:text-6xl leading-tight mb-6">Let’s begin a <span className="italic text-gold">conversation.</span></h2>
          <p className="text-beige/70 font-light leading-relaxed mb-8">
            Share your requirement and our export desk responds within 24 hours with pricing, samples and documentation guidance.
          </p>
          <img src={IMAGES.cargo} alt="Export cargo ship" className="w-full h-56 object-cover" />
        </div>
        <div className="lg:col-span-7">
          <InquiryForm variant="dark" />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <ScrollProgress />
      <Hero />
      <Storytelling />
      <ProcessTimeline />
      <Stats />
      <Manifesto />
      <EditorialMarquee />
      <ProductsPreview />
      <WhyChoose />
      <MapSection />
      <Quality />
      <Industries />
      <Packaging />
      <Sustainability />
      <CertMarquee />
      <Testimonials />
      <Faq />
      <Contact />
    </main>
  );
}
